import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Input, Select, Dropdown, Drawer, Skeleton, Badge } from "antd";
import {
  SearchOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
  UserOutlined,
  MenuOutlined,
  SwapOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import logo from "../assets/logo.png";
const NavbarWrapper = styled.div`
  background: #fff;
  border-bottom: 1px solid #e8e8e8;

  .top-header {
    background: #f8f9fa;
    padding: 8px 0;
    font-size: 12px;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    /* Desktop: left info - center promo - right options */
    .left-links {
      display: flex;
      gap: 20px;
      align-items: center;

      a {
        color: #666;
        text-decoration: none;
        transition: color 0.3s ease;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;

        svg {
          color: #667eea;
          font-size: 11px;
        }

        &:hover {
          color: #000;
        }
      }
    }

    .center-promo {
      flex: 1;
      text-align: center;
      color: #666;
      font-weight: 500;
      font-size: 12px;

      a {
        color: #667eea;
        text-decoration: none;
        font-weight: 600;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .right-links {
      display: flex;
      gap: 15px;
      align-items: center;

      span {
        cursor: pointer;
        transition: color 0.3s ease;
        display: flex;
        align-items: center;
        gap: 5px;
        color: #666;
        font-size: 12px;

        svg {
          font-size: 10px;
        }

        &:hover {
          color: #000;
        }
      }
    }

    @media (max-width: 768px) {
      padding: 6px 0;
      font-size: 10px;

      .left-links {
        gap: 12px;

        a {
          display: none;
        }

        a:first-child {
          display: inline;
        }
      }

      .right-links {
        gap: 10px;
      }
    }

    @media (max-width: 576px) {
      padding: 4px 0;

      .left-links {
        gap: 8px;
      }

      .right-links {
        gap: 8px;
        font-size: 9px;
      }
    }
  }

  .main-header {
    padding: 15px 0;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 15px;
    }

    /* Desktop layout: search left, logo center, actions right */
    .search-bar {
      order: 1;
      flex: 0 0 auto;
      max-width: 400px;
      width: 100%;
      min-width: 0;
      position: relative;

      .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        border-radius: 28px;
        background: #f8f9fa;
        border: 1px solid #ddd;
        padding: 10px 18px;
        transition: all 0.3s ease;

        &:hover,
        &:focus-within {
          border-color: #667eea;
          background: #fff;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
        }

        .search-icon {
          color: #aaa;
          margin-right: 8px;
          font-size: 14px;
          flex-shrink: 0;
        }

        .search-input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 14px;
          color: #333;
          outline: none;
          padding: 0;

          &::placeholder {
            color: #aaa;
          }

          &:focus {
            outline: none;
          }
        }
      }
    }

    .logo {
      order: 2;
      flex: 1;
      text-align: center;
      font-size: 28px;
      font-weight: 700;
      color: #000;
      white-space: nowrap;
      letter-spacing: 1px;
      transition: color 0.3s ease;

      &:hover {
        color: #667eea;
      }
    }

    .header-actions {
      order: 3;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 15px;
      flex-shrink: 0;
      min-width: 280px;

      .action-item {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        padding: 8px 12px;
        border-radius: 6px;
        transition: all 0.3s ease;
        white-space: nowrap;
        position: relative;
        font-size: 13px;
        font-weight: 500;
        color: #333;

        svg {
          font-size: 16px;
          transition: transform 0.3s ease;
        }

        &:hover {
          background: #f0f0f0;

          svg {
            transform: scale(1.1);
          }
        }

        span {
          font-weight: 500;
        }
      }
    }

    .mobile-menu-toggle {
      display: none;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 20px;
      padding: 8px;
      color: #000;
      transition: all 0.3s ease;
      border-radius: 6px;

      &:hover {
        background: #f0f0f0;
      }

      &:active {
        transform: scale(0.95);
      }
    }

    .mobile-cart-icon {
      display: none;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: 6px;
      transition: all 0.3s ease;
      color: #333;
      margin-left: auto;

      svg {
        font-size: 20px;
        transition: transform 0.3s ease;
      }

      &:hover {
        background: #f0f0f0;

        svg {
          transform: scale(1.1);
        }
      }
    }

    @media (max-width: 1024px) {
      gap: 10px;

      .search-bar {
        max-width: 250px;
        margin: 0 10px;
      }

      .header-actions {
        gap: 10px;

        .action-item {
          padding: 6px 8px;
          font-size: 12px;

          span {
            display: none;
          }
        }
      }
    }

    @media (max-width: 768px) {
      padding: 12px 0;
      gap: 8px;

      .logo {
        font-size: 18px;
        flex: 1;
        order: 2;
        text-align: center;
      }

      .search-bar {
        display: none;
      }

      .header-actions {
        display: none;
      }

      .mobile-menu-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        order: 1;
      }

      .mobile-cart-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        order: 3;
        margin-left: auto;
      }
    }

    @media (max-width: 576px) {
      padding: 10px 0;
      gap: 6px;

      .logo {
        font-size: 16px;
      }

      .header-actions {
        gap: 6px;

        .action-item {
          padding: 5px 6px;

          svg {
            font-size: 16px;
          }
        }
      }

      .mobile-menu-toggle {
        padding: 6px;
        font-size: 18px;
      }
    }
  }

  .main-menu {
    background: #000;
    display: block;
    padding: 12px 0;
    border-top: 1px solid #e8e8e8;
    border-bottom: 1px solid #e8e8e8;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .ant-menu {
      background: transparent;
      border: none;
      display: flex;
      gap: 0;
      justify-content: center;

      .ant-menu-item {
        color: #fff !important;
        background: transparent !important;
        border: none !important;
        margin: 0 !important;
        padding: 0 20px !important;
        font-size: 13px;
        font-weight: 600;
        height: auto;
        line-height: 1.5;
        transition: all 0.3s ease;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        display: flex;
        align-items: center;
        border-bottom: 3px solid transparent;

        &:hover {
          background: rgba(255, 255, 255, 0.05) !important;
          color: #fd8e07 !important;
        }

        &.ant-menu-item-selected {
          background: transparent !important;
          color: #fd8e07 !important;
          border-bottom-color: #fd8e07 !important;
        }
      }
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  .mobile-search {
    display: none;
    padding: 12px 20px;
    background: #fff;
    border-bottom: 1px solid #e8e8e8;
    position: relative;

    .search-input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      border-radius: 28px;
      background: #f8f9fa;
      border: 1px solid #ddd;
      padding: 10px 18px;
      transition: all 0.3s ease;

      &:hover,
      &:focus-within {
        border-color: #667eea;
        background: #fff;
        box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
      }

      .search-icon {
        color: #aaa;
        margin-right: 8px;
        font-size: 14px;
        flex-shrink: 0;
      }

      .search-input {
        flex: 1;
        border: none;
        background: transparent;
        font-size: 14px;
        color: #333;
        outline: none;
        padding: 0;
        width: 100%;

        &::placeholder {
          color: #aaa;
        }

        &:focus {
          outline: none;
        }
      }
    }

    @media (max-width: 768px) {
      display: block;
    }
  }

  .mobile-menu-drawer {
    .ant-drawer-header {
      padding: 12px 20px;
      border-bottom: 1px solid #e8e8e8;
      background: #f8f9fa;

      .ant-drawer-title {
        font-weight: 600;
        color: #333;
      }

      .ant-drawer-close {
        color: #333;
      }
    }

    .ant-drawer-content {
      padding: 0;
      background: #fff;
    }

    .menu-section {
      border-bottom: 1px solid #e8e8e8;

      .section-title {
        padding: 14px 20px;
        background: #f8f9fa;
        font-weight: 600;
        color: #333;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .section-links {
        display: flex;
        flex-direction: column;

        a {
          padding: 14px 20px;
          color: #333;
          text-decoration: none;
          border-bottom: 1px solid #f0f0f0;
          transition: all 0.3s ease;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          justify-content: space-between;

          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background: #f8f9fa;
            color: #fd8e07;
            padding-left: 24px;
          }

          &:active {
            background: #e8e8e8;
          }

          .badge {
            background: #667eea;
            color: white;
            border-radius: 12px;
            padding: 2px 8px;
            font-size: 12px;
            font-weight: 600;
          }
        }
      }
    }

    @media (max-width: 576px) {
      .menu-section {
        .section-title {
          padding: 12px 16px;
          font-size: 12px;
        }

        .section-links {
          a {
            padding: 12px 16px;
            font-size: 13px;

            &:hover {
              padding-left: 20px;
            }
          }
        }
      }
    }
  }
`;

const SearchSuggestions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;

  .suggestion-item {
    padding: 12px 18px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 12px;

    &:last-child {
      border-bottom: none;
    }

    .suggestion-image {
      width: 70px;
      height: 70px;
      object-fit: contain;
      border-radius: 8px;
      background: #f8f9fa;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
    }

    .suggestion-content {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .suggestion-title {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: capitalize;
    }

    .suggestion-label {
      font-size: 12px;
      color: #999;
      margin-top: 2px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 500;
    }

    &:hover {
      background: #f8f9fa;

      .suggestion-image {
        transform: scale(1.05);
      }
    }

    &:active {
      background: #f0f0f0;
    }
  }

  .suggestion-skeleton {
    padding: 12px 18px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    gap: 12px;

    &:last-child {
      border-bottom: none;
    }
  }

  .no-results {
    padding: 20px 18px;
    text-align: center;
    color: #999;
    font-size: 13px;
  }
`;

const SearchBarContainer = styled.div`
  position: relative;
`;

const { Search } = Input;
const { Option } = Select;

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLoggedIn = useSelector((state) => state.isLoggedIn);
  const token = useSelector((state) => state.token);
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const fetchCart = useCallback(async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/cart`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.status && data.data?.items) {
        // Set cart count based on number of items
        setCartCount(data.data.items.length);
      } else {
        setCartCount(0);
      }
    } catch (err) {
      console.error("Error fetching cart:", err);
      // dispatch({ type: "logout" });
      setCartCount(0);
    }
  }, [token]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let intv;

    if (token && token !== "") {
      intv = setInterval(() => {
        fetchCart();
      }, 1000);
    }

    return () => {
      if (intv) {
        clearInterval(intv);
      }
    };
  }, [token]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const searchBar = document.querySelector(".search-bar");
      if (searchBar && !searchBar.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [showSuggestions]);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/categories`;
        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }

        const data = await response.json();

        if (data.status && data.data) {
          // Only get top-level categories (parent_id is null or undefined)
          const topLevelCategories = data.data;
          setCategories(topLevelCategories);
        }

        setError(null);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError(err.message);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch cart items with 2-second delay

  const menuItems = [
    { key: "home", label: "Home", path: "/" },
    { key: "shop", label: "Shop", path: "/shop" },
    ...categories.map((cat) => ({
      key: cat.id,
      label: cat.name,
      path: "/shop",
      slug: cat.slug,
      categoryId: cat.id,
    })),
  ];

  const isActive = (item) => {
    if (item.key === "home") {
      return location.pathname === "/";
    }

    // For category items, check if we're on /shop and the category slug matches
    if (item.categoryId) {
      const categoryFromUrl = searchParams.get("category");
      return location.pathname === "/shop" && categoryFromUrl === item.slug;
    }

    return false;
  };

  const currencyMenu = {
    items: [
      { key: "eur", label: "EUR" },
      { key: "usd", label: "USD" },
      { key: "sar", label: "SAR" },
    ],
  };

  const languageMenu = {
    items: [
      { key: "en", label: "English" },
      { key: "fr", label: "Français" },
      { key: "es", label: "Español" },
      { key: "it", label: "Italiano" },
      { key: "de", label: "Deutsch" },
      { key: "ar", label: "العربية" },
    ],
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setShowSuggestions(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Set new timeout - wait 1 second after user stops typing
    setSuggestionLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(
          `${API_BASE_URL}/products/search?q=${encodeURIComponent(value)}`,
        );

        const data = await response.json();

        if (data.status && data.data) {
          // Extract unique categories with a sample product image from each
          const categoryMap = new Map();
          data.data.forEach((product) => {
            if (product.category && !categoryMap.has(product.category.id)) {
              categoryMap.set(product.category.id, {
                ...product.category,
                image: product.image, // Add product image to category
              });
            }
          });
          const uniqueCategories = Array.from(categoryMap.values());
          setSearchSuggestions(uniqueCategories);
          setShowSuggestions(true);
        } else {
          setSearchSuggestions([]);
        }
      } catch (err) {
        console.error("Error fetching search suggestions:", err);
        setSearchSuggestions([]);
      } finally {
        setSuggestionLoading(false);
      }
    }, 1000); // 1 second delay
  };

  const handleSuggestionClick = (category) => {
    navigate(`/shop?category=${category.slug}`, {
      state: { categoryId: category.id },
    });
    setSearchValue("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <NavbarWrapper>
      {/* <div className="top-header">
        <div className="container">
          <div className="left-links">
            <a href="#">Demo Store</a>
          </div>
          <div className="right-links"></div>
        </div>
      </div> */}

      <div className="main-header">
        <div className="container">
          <div className="logo">
            <img
              src={logo}
              alt="Logo"
              style={{ width: "100px", height: "auto" }}
            />
          </div>

          <SearchBarContainer className="search-bar">
            <div className="search-input-wrapper">
              <SearchOutlined className="search-icon" />
              <input
                type="text"
                placeholder="Search Product Here..."
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {showSuggestions && (
              <SearchSuggestions>
                {suggestionLoading ? (
                  <>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="suggestion-skeleton">
                        <Skeleton.Avatar active size={40} shape="square" />
                        <Skeleton
                          active
                          paragraph={false}
                          style={{ flex: 1, width: "200px" }}
                        />
                      </div>
                    ))}
                  </>
                ) : searchSuggestions.length > 0 ? (
                  searchSuggestions.map((category) => (
                    <div
                      key={category.id}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(category)}
                    >
                      <img
                        src={category.image?.url}
                        alt={category.name}
                        className="suggestion-image"
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/50?text=Category";
                        }}
                      />
                      <div className="suggestion-content">
                        <div className="suggestion-title">{category.name}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">No products found</div>
                )}
              </SearchSuggestions>
            )}
          </SearchBarContainer>

          <div className="header-actions">
            {isLoggedIn ? (
              <>
                <div
                  className="action-item"
                  title="My Account"
                  onClick={() => navigate("/my-account")}
                >
                  <UserOutlined style={{ fontSize: "18px" }} />
                  <span>My Account</span>
                </div>

                <div
                  className="action-item"
                  title="Wishlist"
                  onClick={() => navigate("/wishlist")}
                >
                  <HeartOutlined style={{ fontSize: "18px" }} />
                  <span>Wishlist</span>
                </div>

                <div
                  className="action-item"
                  title="Cart"
                  onClick={() => navigate("/cart")}
                >
                  <Badge count={cartCount}>
                    <ShoppingCartOutlined style={{ fontSize: "22px" }} />
                  </Badge>
                </div>
              </>
            ) : (
              <div
                className="action-item"
                title="Sign In"
                onClick={() => navigate("/login")}
              >
                <UserOutlined style={{ fontSize: "18px" }} />
                <span>Sign In</span>
              </div>
            )}
          </div>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(true)}
            title="Menu"
          >
            <MenuOutlined style={{ fontSize: "20px" }} />
          </button>

          {isLoggedIn ? (
            <div className="mobile-cart-icon" onClick={() => navigate("/cart")}>
              <Badge count={cartCount}>
                <ShoppingCartOutlined style={{ fontSize: "20px" }} />
              </Badge>
            </div>
          ) : (
            <div
              className="mobile-cart-icon"
              onClick={() => navigate("/login")}
            >
              <UserOutlined style={{ fontSize: "20px" }} />
            </div>
          )}
        </div>
      </div>

      <SearchBarContainer className="mobile-search">
        <div className="search-input-wrapper">
          <SearchOutlined className="search-icon" />
          <input
            type="text"
            placeholder="Search Product Here..."
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            className="search-input"
          />
        </div>
        {showSuggestions && (
          <SearchSuggestions>
            {suggestionLoading ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="suggestion-skeleton">
                    <Skeleton.Avatar active size={40} shape="square" />
                    <Skeleton
                      active
                      paragraph={false}
                      style={{ flex: 1, width: "200px" }}
                    />
                  </div>
                ))}
              </>
            ) : searchSuggestions.length > 0 ? (
              searchSuggestions.map((category) => (
                <div
                  key={category.id}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(category)}
                >
                  <img
                    src={category.image?.url}
                    alt={category.name}
                    className="suggestion-image"
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/50?text=Category";
                    }}
                  />
                  <div className="suggestion-content">
                    <div className="suggestion-title">{category.name}</div>
                    <div className="suggestion-label">Category</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">No categories found</div>
            )}
          </SearchSuggestions>
        )}
      </SearchBarContainer>

      <div className="main-menu">
        <div className="container">
          <ul
            style={{
              display: "flex",
              gap: 0,
              justifyContent: "center",
              listStyle: "none",
              margin: 0,
              padding: 0,
            }}
          >
            {loading
              ? // Show skeleton loaders while loading
                [1, 2, 3, 4, 5].map((i) => (
                  <li
                    key={`skeleton-${i}`}
                    style={{
                      padding: "0 20px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Skeleton
                      active
                      paragraph={false}
                      style={{ width: "80px", height: "20px" }}
                    />
                  </li>
                ))
              : menuItems.map((item) => {
                  const active = isActive(item);
                  return (
                    <li
                      key={item.key}
                      style={{
                        color: active ? "#fd8e07" : "#fff",
                        background: active
                          ? "rgba(255, 255, 255, 0.05)"
                          : "transparent",
                        border: "none",
                        margin: 0,
                        padding: "0 20px",
                        fontSize: "13px",
                        fontWeight: 600,
                        height: "auto",
                        lineHeight: 1.5,
                        transition: "all 0.3s ease",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (item.categoryId) {
                          navigate(`/shop?category=${item.slug}`, {
                            state: { categoryId: item.categoryId },
                          });
                        } else {
                          navigate(item.path);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.05)";
                          e.currentTarget.style.color = "#fd8e07";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "#fff";
                        }
                      }}
                    >
                      {item.label}
                    </li>
                  );
                })}
            ;
          </ul>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <Drawer
        title="Menu"
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        className="mobile-menu-drawer"
      >
        {/* Categories Section */}
        <div style={{ padding: "0" }}>
          <div
            style={{ paddingBottom: "16px", borderBottom: "1px solid #f0f0f0" }}
          >
            {loading
              ? // Show skeleton loaders while loading
                [1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={`mobile-skeleton-${i}`}
                    style={{
                      padding: "14px 20px",
                      borderBottom: "1px solid #f8f8f8",
                    }}
                  >
                    <Skeleton
                      active
                      paragraph={false}
                      style={{ width: "100%", height: "20px" }}
                    />
                  </div>
                ))
              : menuItems.map((item, index) => {
                  const active = isActive(item);
                  return (
                    <div
                      key={item.key}
                      style={{
                        padding: "14px 20px",
                        borderBottom:
                          index < menuItems.length - 1
                            ? "1px solid #f8f8f8"
                            : "none",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        transition: "background 0.2s ease",
                        background: active ? "#f0f0f0" : "transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.background = "#f9f9f9";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                      onClick={() => {
                        if (item.categoryId) {
                          navigate(`/shop?category=${item.slug}`, {
                            state: { categoryId: item.categoryId },
                          });
                        } else {
                          navigate(item.path);
                        }
                        setMobileMenuOpen(false);
                      }}
                    >
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: "500",
                          color: active ? "#667eea" : "#333",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {item.label}
                      </span>
                      {index === 0 && (
                        <span
                          style={{
                            fontSize: "18px",
                            color: "#667eea",
                            fontWeight: "bold",
                          }}
                        >
                          +
                        </span>
                      )}
                    </div>
                  );
                })}
          </div>
        </div>

        {/* My Account / Sign In Section */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <button
            style={{
              width: "100%",
              padding: "12px 16px",
              background: "#667eea",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "14px",
              color: "#fff",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#5568d3";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#667eea";
            }}
            onClick={() => {
              isLoggedIn ? navigate("/my-account") : navigate("/login");
              setMobileMenuOpen(false);
            }}
          >
            {isLoggedIn ? "My Account" : "Sign In"}
          </button>
        </div>

        {/* Wishlist & Compare Section - Only show when logged in */}
        {isLoggedIn && (
          <div
            style={{
              padding: "16px 20px",
              display: "flex",
              gap: "12px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <button
              style={{
                flex: 1,
                padding: "12px 16px",
                background: "#fff",
                border: "1px solid #e8e8e8",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
                color: "#333",
                transition: "all 0.3s ease",
              }}
              onClick={() => navigate("/wishlist")}
              onMouseEnter={(e) => {
                e.target.style.background = "#f8f9fa";
                e.target.style.borderColor = "#667eea";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#fff";
                e.target.style.borderColor = "#e8e8e8";
              }}
            >
              Wishlist
            </button>
            {/* <button
            style={{
              flex: 1,
              padding: "12px 16px",
              background: "#fff",
              border: "1px solid #e8e8e8",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "14px",
              color: "#333",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#f8f9fa";
              e.target.style.borderColor = "#667eea";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
              e.target.style.borderColor = "#e8e8e8";
            }}
          >
            Compare
          </button> */}
          </div>
        )}
      </Drawer>
    </NavbarWrapper>
  );
};

export default Navbar;
